// src/pages/AccountingDirections.js
//
// The "Directions" tab of the Accounting page: how to add, update and delete
// everything, and where each number comes from. Text only — edit the GUIDES
// list below to change the directions. Nothing here reads or writes Firestore.

import React, { useMemo, useState } from "react";

const GUIDES = [
  {
    key: "start",
    title: "Start Here",
    subtitle: "Set up once, then the routine",
    intro:
      "Every Accounting page reads the same records. Enter a number ONCE, in the place it belongs, and it shows up everywhere else. Never type the same payment or bill into two pages.",
    sections: [
      {
        heading: "One-time setup",
        items: [
          "Staff page (Supervisor Portal → More → Staff): for every tech, fill in Work Hourly Rate and Paid Hours Per Day (or a Flat Day Rate). Tech Daily Pay and every labor number use these.",
          "Bank Statement → + Add Bank or Card: add each bank account and credit card once.",
          "Profit & Profit Sharing: add the people who get profit sharing and their percent, then Save list.",
          "Cash Plan: unlock with the access code and add your buckets, monthly bills and debts.",
        ],
      },
      {
        heading: "Every day",
        items: [
          "Techs Clock In / Out and Check In / Out of jobs on the Home page. That fills Tech Daily Pay automatically.",
          "Tech Daily Pay: open the day, check it, pick jobs for anyone with \"No job picked\", and press Save Day.",
          "Completed jobs go through the Complete Job & Create Invoice form → Approved Invoices. Review and email invoices from Approved Invoices.",
        ],
      },
      {
        heading: "When a customer pays",
        items: [
          "Press Paid on the invoice in Approved Invoices — OR — + Add Payment on Deposits — OR — + Add Check on the Job Estimate Spreadsheet. Pick ONE. All three save to the same Deposits list.",
          "If Deposits shows a yellow \"possible duplicate payments\" box, press Review duplicates and delete the extra copy.",
        ],
      },
      {
        heading: "Every month",
        items: [
          "Upload every bank and card statement on Bank Statement (tick Personal / Reimbursable where needed).",
          "Check Bank Statement → Banks & Cards shows ✓ for every account.",
          "Open Profit & Profit Sharing, pick the month, clear any red warnings, then Record payout.",
          "Accounting Exports → Refresh Data → Download Excel Report for your accountant. Read the Reconciliation sheet first.",
        ],
      },
    ],
  },
  {
    key: "flow",
    title: "Where Numbers Come From",
    subtitle: "So nothing is counted twice",
    intro: "Each kind of number has one home. Change it at its home and every page updates.",
    table: [
      ["Customer payments (money in)", "Deposits (also created by Paid on Approved Invoices and + Add Check on the Spreadsheet)", "Spreadsheet, Profit, Cash Plan, Exports, Overview"],
      ["Invoices", "Complete Job & Create Invoice form → Approved Invoices", "Spreadsheet, Deposits, Cash Plan, Exports"],
      ["Tech labor (actual)", "Tech Daily Pay", "Spreadsheet (per job), Expenses (payroll), Cash Plan, Profit (payroll check), Exports, Overview"],
      ["Pay rates", "Staff page", "Tech Daily Pay, Spreadsheet estimates, Cash Plan projections"],
      ["Money out (bank & cards)", "Bank Statement", "Profit, Exports, Overview"],
      ["Subcontractor pay, bills, bank deductions, extra employee pay", "Expenses", "Spreadsheet, Cash Plan, Exports, Overview"],
      ["Job prices & estimates", "Job Estimate Spreadsheet (and the job itself)", "Overview, Exports, Cash Plan"],
    ],
    sections: [
      {
        heading: "The rules that stop double counting",
        items: [
          "Profit is based on PAYMENTS (Deposits), not invoices and not bank deposits. A paid invoice counts once, through its payment.",
          "A recurring contract is one job. A payment that names a visit pays that visit; any other payment pays the oldest unpaid visit first. One check never marks every month paid.",
          "Job labor = Tech Daily Pay when a day has been saved for that job; otherwise the estimate (hourly rate × hours per day). Never both.",
          "Tech Daily Pay that isn't tied to a job is counted once, as general labor for the period.",
          "Profit & Profit Sharing takes payroll from the bank statement. Tech Daily Pay is shown next to it as a check — it is NOT subtracted again.",
          "On Expenses, a Regular/Overtime paycheck for someone already on Tech Daily Pay that month is greyed out and not added again. Bonus and Other pay always count.",
        ],
      },
    ],
  },
  {
    key: "dailypay",
    title: "Tech Daily Pay",
    subtitle: "Who worked, hours, day pay, jobs",
    tab: "dailypay",
    intro:
      "One saved record per day. It is filled in for you from Clock In / Out (hours, lunch removed) and job Check Ins (which jobs). Pay = Flat Day Rate if the Staff page has one, otherwise Work Hourly Rate × hours.",
    sections: [
      {
        heading: "Add a day",
        items: [
          "Pick the date (◀ ▶ or the date box). A new day is pre-filled from the clock and check-ins — the badge says \"Unsaved changes\".",
          "Tick Worked for anyone missing, adjust Hours, and press Pick jobs to choose the jobs they worked (their pay is split evenly over those jobs).",
          "Press Save Day.",
        ],
      },
      {
        heading: "Update a day",
        items: [
          "Open the date, change Worked / Hours / Jobs, press Save Day.",
          "To pay a different amount for the day, type it in Day Pay. Press \"use calculated\" to go back to the automatic amount.",
          "Fill from Clock In / Check-ins re-reads the clock and check-ins for that day (your typed pay amounts are kept).",
        ],
      },
      {
        heading: "Delete a day",
        items: ["Open the date and press Delete Day. It disappears from every Accounting page."],
      },
      {
        heading: "Good to know",
        items: [
          "\"No rate on Staff page\" means that person has no pay rate yet — fix it on the Staff page.",
          "Hide removes office people who never do field work from the list (Show hidden staff brings them back).",
          "The Saved Tech Daily Pay box shows this week's or month's totals per person.",
        ],
      },
    ],
  },
  {
    key: "jobestimate",
    title: "Job Estimate Spreadsheet",
    subtitle: "Every job's price, costs and profit",
    tab: "jobestimate",
    intro:
      "One row per job. Recurring contracts show one row — press Show Dates to see each visit. Revenue is the invoice once a visit is invoiced, otherwise the estimate. Labor is Tech Daily Pay once a day is saved, otherwise the estimate.",
    sections: [
      {
        heading: "Add",
        items: [
          "+ Add Check: a customer check for that job (on a visit row it pays that visit). Saves to Deposits.",
          "+ Split Check: one check across several jobs or visits.",
          "+ Add Receipt, Sub Pay / CO, Staff Pay / % for job costs.",
          "+ Add Bulk Material, + Monthly Expense, + Add Bulk Income for amounts not tied to one job.",
        ],
      },
      {
        heading: "Update",
        items: [
          "Change the cells, then press Save Main (one-time job) or Save This Date (one visit of a recurring job).",
          "Press Edit next to a check or receipt.",
          "\"Invoice payment\" and \"Unapplied payment\" lines come from Approved Invoices / Deposits — change them there.",
        ],
      },
      {
        heading: "Delete",
        items: [
          "Press Delete next to a check, receipt or payment.",
          "Monthly expense: Delete From Date stops it going forward; Delete Total Expense removes it completely.",
        ],
      },
    ],
  },
  {
    key: "bank",
    title: "Bank Statement",
    subtitle: "Money out, statements, personal & reimbursable",
    tab: "bank",
    intro:
      "Upload every bank account and credit card statement each month. Money out comes from here. Money in on a statement is only a record — customer payments come from Deposits.",
    sections: [
      {
        heading: "Add",
        items: [
          "+ Add Bank or Card: bank name, last 4, nickname, type (Operating, Credit Card, Personal) → Add Bank.",
          "Upload a statement: choose the bank/card under \"Choose Bank / Last 4\", then the PDF. Review the rows: tick Personal (not business), Reimbursable (owner money or a loan to pay back), pick a contractor payment or a job/visit for materials, set Recurring only for monthly bills → Import Selected Rows. Duplicates are skipped.",
          "If a PDF won't read: Show / Paste Raw Statement Text → paste → Re-Analyze Text.",
          "One line by hand: Show Manual Bank Statement Entry → Save Bank Statement Entry.",
          "Monthly Summary: pick the month → Enter month → type money in/out and categories → Save month.",
        ],
      },
      {
        heading: "Update",
        items: [
          "Show Recent Entries → open the month → open the statement → Edit → Save Changes. Personal / Reimbursable ticks save as soon as you click them.",
          "Monthly Summary: Edit on the account → Save month.",
        ],
      },
      {
        heading: "Delete",
        items: [
          "Show Recent Entries → month → statement → Delete. Everything that line created goes with it (ledger entry, bill, job material, contractor link).",
          "Monthly Summary: open the month → Delete month.",
        ],
      },
      {
        heading: "Payroll check",
        items: ["The \"Payroll vs Tech Daily Pay\" table only compares the two. It never changes profit."],
      },
    ],
  },
  {
    key: "deposits",
    title: "Deposits",
    subtitle: "Customer payments",
    tab: "deposits",
    intro: "Every customer payment lives here, including ones entered from Approved Invoices and the Spreadsheet.",
    sections: [
      {
        heading: "Add",
        items: [
          "+ Add Payment → pick the property. If it pays a specific invoice, choose it under \"Pay an open invoice\" (fills WO, visit and amount). Otherwise pick the Work Order and, for a recurring job, the visit it pays. Enter check # and date → Save.",
          "If it warns the payment may already be entered: \"Don't save\" or \"It's a different payment — save\".",
          "Many at once: Bulk Upload → Download template → fill it in → upload → check the rows → Save.",
        ],
      },
      {
        heading: "Update",
        items: ["Edit on the row → change it → Save Changes. The invoice's Paid status updates by itself."],
      },
      {
        heading: "Delete",
        items: [
          "Delete on the row → confirm. The invoice goes back to unpaid if it's no longer covered.",
          "Duplicates: Review duplicates → tick Keep on the real one → Delete the extras (or Not a duplicate).",
        ],
      },
    ],
  },
  {
    key: "expenses",
    title: "Expenses",
    subtitle: "Subs, extra pay, monthly bills, bank deductions",
    tab: "expenses",
    intro:
      "Pick the dates with From / To. The Tech Daily Pay tab is read-only payroll from the Tech Daily Pay page and is included in the total.",
    sections: [
      {
        heading: "Add",
        items: ["Pick a tab (Subcontractor Pay, Employee Pay, Monthly Bills or Bank Deductions) → + Add… → fill it in → Save."],
      },
      {
        heading: "Update",
        items: [
          "Edit on the row → change it → Save.",
          "Tech Daily Pay rows: press Open Tech Daily Pay and change the day there.",
          "A greyed-out Regular/Overtime paycheck is already covered by Tech Daily Pay. To count it anyway: Edit → Tech Daily Pay overlap → Separate.",
        ],
      },
      {
        heading: "Delete",
        items: ["Delete on the row → confirm. It is also removed from the Overview."],
      },
    ],
  },
  {
    key: "profit",
    title: "Profit & Profit Sharing",
    subtitle: "Profit by month, quarter or year",
    tab: "profit",
    intro:
      "Money in = payments on Deposits. Money out = bank statements. Pick Month, Quarter or Year and the period.",
    sections: [
      {
        heading: "Fix warnings first",
        items: [
          "Upload any missing statements (Bank Statement).",
          "Review possible duplicate payments (Deposits).",
          "\"Marked Paid with no payment\": record the payment with Paid on Approved Invoices.",
          "Compare Tech Daily Pay with bank payroll in the payroll check (it is never subtracted).",
        ],
      },
      {
        heading: "Add / update / delete",
        items: [
          "Add a person: Add employee… → Add → type their percent → Save list.",
          "Change a percent: edit it → Save list. Remove someone: × → Save list.",
          "Record a payout: Record payout → confirm. Delete one: Delete next to it under Recorded payouts.",
          "Download Excel saves the period.",
        ],
      },
    ],
  },
  {
    key: "cashplan",
    title: "Cash Plan",
    subtitle: "What's coming in and going out this month",
    tab: "cashplan",
    intro:
      "Enter the access code to unlock (Lock Page when done). Money Coming In = unpaid invoices + visits still scheduled this month + payments received. Payroll: Projected vs Actual compares the plan with Tech Daily Pay saved this month.",
    sections: [
      {
        heading: "Add",
        items: ["Open Buckets, Monthly Bills or Debts → fill in the top row → Add (confirm)."],
      },
      {
        heading: "Update",
        items: [
          "Change the row's fields → Save.",
          "Employees: type an override hourly rate → Save Rate. Hide Employee leaves someone out of the payroll plan.",
        ],
      },
      {
        heading: "Delete",
        items: [
          "Buckets and debts: Delete.",
          "Monthly bills can't be deleted here — press Stop Payment so it stops counting after its last payment date.",
        ],
      },
      {
        heading: "Excel",
        items: ["Excel Tools: download the template, upload it, save the bulk upload, or download the current plan."],
      },
    ],
  },
  {
    key: "exports",
    title: "Accounting Exports",
    subtitle: "One Excel workbook for the accountant",
    tab: "exports",
    intro:
      "Read-only. Pick Start / End dates (End blank = today) and how far to look ahead, press Refresh Data, then Download Excel Report.",
    sections: [
      {
        heading: "What's inside",
        items: [
          "Summary, Money Received, Money Paid Out, Tech Daily Pay (by day, person and job), Who Owes Me, Who I Owe, Potential Income, Job Profitability, Reconciliation and more.",
          "Open the Reconciliation sheet first — each warning says what to fix and where.",
        ],
      },
      {
        heading: "Fixing a number",
        items: [
          "Nothing is added, changed or deleted here. Change it where it was entered (Approved Invoices, Deposits, Expenses, Bank Statement, Job Estimate Spreadsheet, Daily Spending, Staff or Tech Daily Pay), then press Refresh Data.",
        ],
      },
    ],
  },
  {
    key: "overview",
    title: "Overview",
    subtitle: "Financials dashboard",
    tab: "overview",
    intro: "Choose Day, Week, Month or Year and a date.",
    sections: [
      {
        heading: "Jobs tab",
        items: [
          "Type a one-time job's price in the Price box and click away to save it.",
          "Edit changes that job's materials, labor estimate, misc and overhead.",
          "Recurring job prices come from their invoices. Labor marked \"actual\" comes from Tech Daily Pay — fix it on that day.",
        ],
      },
      {
        heading: "Transactions & Bank Statements tabs",
        items: [
          "Add Transaction is only for one-off manual entries — deposits, pay and bills show up here automatically from their own pages.",
          "Edit or delete an entry on the page it came from. Orange bank rows are records only and are not counted.",
        ],
      },
    ],
  },
];

const card = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  marginBottom: 14,
};

export default function AccountingDirections({ onOpenTab }) {
  const [activeKey, setActiveKey] = useState("start");
  const [search, setSearch] = useState("");

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const hits = [];
    GUIDES.forEach((guide) => {
      (guide.sections || []).forEach((section) => {
        section.items.forEach((item) => {
          if (`${guide.title} ${section.heading} ${item}`.toLowerCase().includes(q)) {
            hits.push({ guide, heading: section.heading, item });
          }
        });
      });
    });
    return hits;
  }, [search]);

  const guide = GUIDES.find((g) => g.key === activeKey) || GUIDES[0];

  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start", textAlign: "left" }}>
      <div style={{ flex: "0 0 240px", maxWidth: "100%" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the directions…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "9px 10px",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            marginBottom: 10,
          }}
        />
        {GUIDES.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => {
              setActiveKey(g.key);
              setSearch("");
            }}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              margin: "0 0 6px",
              padding: "9px 12px",
              borderRadius: 10,
              border: g.key === activeKey && !results ? "1px solid #1d4ed8" : "1px solid #e2e8f0",
              background: g.key === activeKey && !results ? "#eff6ff" : "#fff",
              cursor: "pointer",
              color: "#0f172a",
            }}
          >
            <div style={{ fontWeight: 800 }}>{g.title}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{g.subtitle}</div>
          </button>
        ))}
      </div>

      <div style={{ flex: "1 1 420px", minWidth: 0 }}>
        {results ? (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>
              {results.length} result{results.length === 1 ? "" : "s"} for "{search}"
            </h3>
            {results.map((hit, index) => (
              <div key={index} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {hit.guide.title} → {hit.heading}
                </div>
                <div>{hit.item}</div>
              </div>
            ))}
            {!results.length && <p>Nothing found. Try another word.</p>}
          </div>
        ) : (
          <>
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0 }}>{guide.title}</h2>
                {guide.tab && typeof onOpenTab === "function" && (
                  <button
                    type="button"
                    onClick={() => onOpenTab(guide.tab)}
                    style={{
                      marginLeft: "auto",
                      background: "#1d4ed8",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 14px",
                      fontWeight: 700,
                      cursor: "pointer",
                      width: "auto",
                    }}
                  >
                    Open {guide.title} →
                  </button>
                )}
              </div>
              <p style={{ color: "#334155", marginBottom: 0 }}>{guide.intro}</p>
            </div>

            {guide.table && (
              <div style={{ ...card, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr>
                      {["Number", "Entered on (its home)", "Shows up on"].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: 8,
                            background: "#f1f5f9",
                            borderBottom: "1px solid #e2e8f0",
                            fontSize: 13,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {guide.table.map((row) => (
                      <tr key={row[0]}>
                        {row.map((cell, i) => (
                          <td
                            key={i}
                            style={{
                              padding: 8,
                              borderBottom: "1px solid #f1f5f9",
                              fontWeight: i === 0 ? 700 : 400,
                              verticalAlign: "top",
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(guide.sections || []).map((section) => (
              <div key={section.heading} style={card}>
                <h3 style={{ marginTop: 0 }}>{section.heading}</h3>
                <ol style={{ margin: 0, paddingLeft: 22, lineHeight: 1.55 }}>
                  {section.items.map((item, index) => (
                    <li key={index} style={{ marginBottom: 6 }}>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
